import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { BellRing, Plus, Trash2, Sparkles, Tag, Layers } from "lucide-react";
import { TCGS, type TCG, type GradingCo } from "@/lib/marketplace";
import { toast } from "sonner";

interface PriceAlertSearch {
  prefill?: number;
  cardName?: string;
  tcg?: string;
  condition?: string;
  gradingCo?: string;
  minGrade?: number;
}

export const Route = createFileRoute("/_authenticated/price-alerts")({
  head: () => ({ meta: [{ title: "Price Alerts — Want To Buy" }] }),
  validateSearch: (search: Record<string, unknown>): PriceAlertSearch => ({
    prefill: search.prefill ? Number(search.prefill) : undefined,
    cardName: typeof search.cardName === "string" ? search.cardName : undefined,
    tcg: typeof search.tcg === "string" ? search.tcg : undefined,
    condition: typeof search.condition === "string" ? search.condition : undefined,
    gradingCo: typeof search.gradingCo === "string" ? search.gradingCo : undefined,
    minGrade: search.minGrade ? Number(search.minGrade) : undefined,
  }),
  component: PriceAlertsPage,
});

type AlertKind = "structured" | "keyword" | "combined";

interface Alert {
  id: string;
  cardName?: string;
  tcg?: TCG;
  condition: "raw" | "graded" | "any";
  gradingCo?: GradingCo;
  minGrade?: number;
  minTargetPrice?: number;
  keywords?: string[];
  enabled: boolean;
  email: boolean;
}

function classify(a: Pick<Alert, "cardName" | "tcg" | "gradingCo" | "minGrade" | "minTargetPrice" | "condition" | "keywords">): AlertKind {
  const hasStructured = !!a.cardName || !!a.gradingCo || a.minGrade != null || a.minTargetPrice != null || (a.condition && a.condition !== "any");
  const hasKeywords = !!a.keywords && a.keywords.length > 0;
  if (hasStructured && hasKeywords) return "combined";
  if (hasKeywords) return "keyword";
  return "structured";
}

const STARTER: Alert[] = [
  { id: "a1", cardName: "Charizard", tcg: "Pokémon", condition: "graded", gradingCo: "PSA", minGrade: 9, minTargetPrice: 3500, enabled: true, email: true },
  { id: "a2", cardName: "Black Lotus", tcg: "Magic: The Gathering", condition: "graded", gradingCo: "BGS", minGrade: 8, minTargetPrice: 30000, enabled: true, email: false },
  { id: "a3", tcg: "Pokémon", condition: "any", keywords: ["Moonbreon", "Alt Art", "Umbreon VMAX"], enabled: true, email: true },
];

function parseKeywords(input: string): string[] {
  return input
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function PriceAlertsPage() {
  const search = Route.useSearch();
  const [alerts, setAlerts] = useState<Alert[]>(STARTER);
  const [emailGlobal, setEmailGlobal] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<Partial<Alert>>({ tcg: "Pokémon", condition: "any" });
  const [keywordsInput, setKeywordsInput] = useState("");

  useEffect(() => {
    if (search.prefill) {
      setDraft({
        cardName: search.cardName,
        tcg: (search.tcg as TCG) ?? "Pokémon",
        condition: (search.condition as Alert["condition"]) ?? "any",
        gradingCo: search.gradingCo as GradingCo | undefined,
        minGrade: search.minGrade,
      });
      setKeywordsInput("");
      setShowForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.prefill]);

  function toggle(id: string) {
    setAlerts((a) => a.map((x) => x.id === id ? { ...x, enabled: !x.enabled } : x));
  }
  function remove(id: string) {
    setAlerts((a) => a.filter((x) => x.id !== id));
  }
  function add() {
    const keywords = parseKeywords(keywordsInput);
    const hasStructured = !!draft.cardName;
    if (!hasStructured && keywords.length === 0) {
      toast.error("Add a card name or at least one keyword");
      return;
    }
    const a: Alert = {
      id: `a${Date.now()}`,
      cardName: draft.cardName || undefined,
      tcg: (draft.tcg as TCG) ?? "Pokémon",
      condition: (draft.condition as Alert["condition"]) ?? "any",
      gradingCo: draft.gradingCo as GradingCo | undefined,
      minGrade: draft.minGrade,
      minTargetPrice: draft.minTargetPrice,
      keywords: keywords.length > 0 ? keywords : undefined,
      enabled: true,
      email: emailGlobal,
    };
    setAlerts((p) => [a, ...p]);
    setDraft({ tcg: "Pokémon", condition: "any" });
    setKeywordsInput("");
    setShowForm(false);
    toast.success("Alert created");
  }

  return (
    <AppShell>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <BellRing className="h-7 w-7 text-primary" /> Price Alerts
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Get notified when a new buy order matches your criteria or saved keywords.</p>
        </div>
        <button onClick={() => setShowForm((s) => !s)} className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm">
          <Plus className="h-4 w-4" /> New Alert
        </button>
      </div>

      <div className="panel mb-4 flex items-center justify-between gap-3 p-4">
        <div>
          <div className="text-sm font-semibold">Email notifications</div>
          <div className="text-xs text-muted-foreground">Receive email when matching buy orders are posted.</div>
        </div>
        <Switch on={emailGlobal} onClick={() => setEmailGlobal((e) => !e)} />
      </div>

      {showForm && (
        <div className="panel mb-4 space-y-3 p-5">
          <h2 className="font-display text-lg font-bold">New alert</h2>
          <p className="text-xs text-muted-foreground">
            Fill any combination of structured fields and keywords. A match on either side triggers a notification.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Card name (optional if using keywords)">
              <input value={draft.cardName ?? ""} onChange={(e) => setDraft({ ...draft, cardName: e.target.value })} className="input-base" placeholder="e.g. Charizard" />
            </Field>
            <Field label="TCG">
              <select value={draft.tcg} onChange={(e) => setDraft({ ...draft, tcg: e.target.value as TCG })} className="input-base">
                {TCGS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Condition">
              <select value={draft.condition} onChange={(e) => setDraft({ ...draft, condition: e.target.value as Alert["condition"] })} className="input-base">
                <option value="any">Any</option>
                <option value="raw">Raw</option>
                <option value="graded">Graded</option>
              </select>
            </Field>
            <Field label="Grading company (optional)">
              <select value={draft.gradingCo ?? ""} onChange={(e) => setDraft({ ...draft, gradingCo: (e.target.value || undefined) as GradingCo })} className="input-base">
                <option value="">Any</option>
                <option value="PSA">PSA</option>
                <option value="BGS">BGS</option>
                <option value="CGC">CGC</option>
              </select>
            </Field>
            <Field label="Min grade (optional)">
              <input type="number" step="0.5" min={1} max={10} value={draft.minGrade ?? ""} onChange={(e) => setDraft({ ...draft, minGrade: e.target.value ? Number(e.target.value) : undefined })} className="input-base" />
            </Field>
            <Field label="Min target price USD (optional)">
              <input type="number" min={0} value={draft.minTargetPrice ?? ""} onChange={(e) => setDraft({ ...draft, minTargetPrice: e.target.value ? Number(e.target.value) : undefined })} className="input-base" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Keywords (comma-separated, optional)">
                <input
                  value={keywordsInput}
                  onChange={(e) => setKeywordsInput(e.target.value)}
                  className="input-base"
                  placeholder='e.g. Moonbreon, Alt Art, Umbreon VMAX'
                />
              </Field>
              <p className="mt-1 text-[11px] text-muted-foreground">
                We'll match these case-insensitively against each new buy order's card name, set, card number, and notes. Any hit triggers a notification.
              </p>
              {parseKeywords(keywordsInput).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {parseKeywords(keywordsInput).map((k) => (
                    <span key={k} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px]">
                      <Tag className="h-3 w-3" /> {k}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="btn-ghost px-3.5 py-1.5 text-sm">Cancel</button>
            <button onClick={add} className="btn-primary px-3.5 py-1.5 text-sm">Save alert</button>
          </div>
        </div>
      )}

      {alerts.length === 0 ? (
        <div className="panel p-10 text-center text-sm text-muted-foreground">
          No price alerts set — add an alert to be notified when someone posts a buy order for a card you want to sell.
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map((a) => {
            const kind = classify(a);
            return (
              <div key={a.id} className="panel flex flex-wrap items-start justify-between gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-display text-base font-bold">
                      {a.cardName ?? (a.keywords?.[0] ? `"${a.keywords[0]}"` : "Untitled alert")}
                    </div>
                    <KindBadge kind={kind} />
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {a.tcg ?? "Any TCG"} · {a.condition === "any" ? "Any condition" : a.condition}
                    {a.gradingCo ? ` · ${a.gradingCo}${a.minGrade ? ` ${a.minGrade}+` : ""}` : ""}
                    {a.minTargetPrice ? ` · min $${a.minTargetPrice.toLocaleString()}` : ""}
                  </div>
                  {a.keywords && a.keywords.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Keywords:</span>
                      {a.keywords.map((k) => (
                        <span key={k} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px]">
                          <Tag className="h-3 w-3" /> {k}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Switch on={a.enabled} onClick={() => toggle(a.id)} />
                  <button onClick={() => remove(a.id)} className="grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <style>{`.input-base { width:100%; height:2.5rem; border-radius:0.375rem; border:1px solid var(--color-border); background: var(--color-background); padding: 0 0.75rem; font-size: 0.875rem; color: var(--color-foreground); }
      .input-base:focus { outline:none; border-color: var(--color-primary); }`}</style>
    </AppShell>
  );
}

function KindBadge({ kind }: { kind: AlertKind }) {
  const config = {
    structured: { label: "Structured", icon: Layers, cls: "border-primary/40 bg-primary/10 text-primary" },
    keyword: { label: "Keyword", icon: Tag, cls: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" },
    combined: { label: "Combined", icon: Sparkles, cls: "border-violet-500/40 bg-violet-500/10 text-violet-600 dark:text-violet-400" },
  }[kind];
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${config.cls}`}>
      <Icon className="h-3 w-3" /> {config.label}
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function Switch({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      role="switch"
      aria-checked={on}
      className={`relative h-6 w-11 rounded-full border transition ${on ? "border-primary bg-primary" : "border-border bg-muted"}`}
    >
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${on ? "left-5" : "left-0.5"}`} />
    </button>
  );
}
